<template>
<div>
    <div
        class="nch--search-container d-flex justify-content-between align-items-center"
        :class="{'nch-search--fixed': layout && !noFixed}"
    >
        <div class="container d-flex justify-content-between align-items-center">
            <div v-if="showSearch" class="d-flex">
                <i class="fas fa-search mt-2" aria-hidden="true"/>
                <input
                    @input="$emit('searchInput', $event.target.value)"
                    :placeholder="$t('common.search')"
                    :aria-label="$t('common.search')"
                    class="bg-transparent form-control form-control-sm ml-3 w-75 nch--search-input"
                    type="text"
                />
            </div>
            <div
                v-if="filters || categories || visibleFacets.length"
                class="d-flex justify-content-between align-items-center"
            >
                <CurrencySelect v-if="currency && hasBilling" @currency="$emit('currency', $event)" />
                <a
                    v-if="filters || categories || visibleFacets.length"
                    @click="show = !show"
                    href="#"
                    role="button"
                    id="btnShowFilter"
                    class="nav-link d-flex align-items-center"
                >
                    <i class="mdi mdi-24px mdi-tune-vertical"/>
                    <span :class="hideOnSmall"> {{ $t('cart.filter') }} </span>
                </a>
            </div>
        </div>
    </div>
    <collapse-transition :duration="250">
        <div
            v-show="show"
            :class="{'filter-container--fixed': layout && !noFixed}"
            class="filter-container"
        >
            <div class="container">
                <h2>{{ $t('cart.filter_by') }}:</h2>
                <div class="d-flex flex-wrap my-2">
                    <div v-if="filters" class="col-12 col-sm-6 col-md-4">
                        <i class="mdi mdi-format-list-bulleted-type"></i>
                        <span class="font-weight-bold">
                            {{ $t('cart.plans.types') }}
                        </span>
                        <div class="form-check mt-2">
                            <base-checkbox
                                class="form-check-input"
                                :value="2"
                                id="public"
                                v-model="planType"
                            >
                                {{ $t('cart.plans.public') }}
                            </base-checkbox>
                        </div>
                        <div class="form-check">
                            <base-checkbox
                                class="form-check-input"
                                :value="1"
                                id="free"
                                v-model="planType"
                            >
                                {{ $t('cart.plans.free') }}
                            </base-checkbox>
                        </div>
                        <div class="form-check" v-if="hasBilling">
                            <base-checkbox
                                class="form-check-input"
                                :value="3"
                                id="unique"
                                v-model="planType"
                            >
                                {{ $t('cart.plans.single_purchase') }}
                            </base-checkbox>
                        </div>
                        <div class="form-check" v-if="hasBilling">
                            <base-checkbox
                                class="form-check-input"
                                :value="4"
                                id="recurrent"
                                v-model="planType"
                            >
                                {{ $t('cart.plans.recurrence') }}
                            </base-checkbox>
                        </div>
                    </div>
                    <div v-if="categories" class="col-12 col-sm-6 col-md-4">
                        <i class="mdi mdi-label"/>
                        <span class="font-weight-bold mb-5">
                            {{ $t('cart.plans.categories') }}
                        </span>
                        <div class="category-parent">
                            <div
                                v-for="(category,i) in categories"
                                :key="i"
                                class="form-check"
                            >
                                <base-checkbox
                                    v-model="planCategory"
                                    :value="category.id"
                                    :disabled="planCategory.find(x => !x)"
                                    class="form-check-input"
                                >
                                    {{ $findTranslation(category, language).name }}
                                </base-checkbox>
                            </div>
                            <div class="form-check">
                                <base-checkbox
                                    @input="e => removeChecks(e)"
                                    :value="[]"
                                    class="form-check-input"
                                >
                                    {{ $t('cart.plans.no_category') }}
                                </base-checkbox>
                            </div>
                        </div>
                    </div>
                    <!-- Facetas (Modalidade, Público Alvo, Acesso, Ano…) -->
                    <div
                        v-for="facet in visibleFacets"
                        :key="facet.key"
                        class="col-12 col-sm-6 col-md-4"
                    >
                        <i class="mdi mdi-filter-variant"/>
                        <span class="font-weight-bold mb-5">
                            {{ facet.label }}
                        </span>
                        <div class="facet-parent">
                            <div
                                v-for="opt in facet.values"
                                :key="opt.value"
                                class="form-check"
                            >
                                <base-checkbox
                                    :value="opt.value"
                                    :model-value="facetValues(facet.key)"
                                    class="form-check-input"
                                    @update:modelValue="val => onFacetInput(facet.key, val)"
                                >
                                    {{ opt.label }}
                                </base-checkbox>
                            </div>
                        </div>
                    </div>
                    <!-- Filtro por Ano -->
                    <div
                        v-if="availableYears && availableYears.length >= 1"
                        class="col-12 col-sm-6 col-md-4"
                    >
                        <i class="mdi mdi-calendar"/>
                        <span class="font-weight-bold mb-5">
                            Ano
                        </span>
                        <div class="mt-2">
                            <select
                                class="form-control form-control-sm nch-year-select"
                                :value="selectedYear"
                                @change="$emit('yearFilter', $event.target.value)"
                            >
                                <option value="">Todos os anos</option>
                                <option
                                    v-for="y in availableYears"
                                    :key="y"
                                    :value="y"
                                >{{ y }}</option>
                            </select>
                        </div>
                    </div>
                </div>
                <h2 v-if="filters && hasBilling">{{ $t('cart.order_by') }}:</h2>
                <div v-if="filters && hasBilling" class="d-flex flex-wrap my-2">
                    <div class="col-12 col-sm-6 col-md-4">
                        <i class="mdi mdi-currency-usd"/>
                        <span class="font-weight-bold mb-5">
                            {{ $t('cart.plans.value') }}
                        </span>
                        <div class="form-check">
                            <base-radio name="ASC" v-model="price">
                                {{ $t('cart.min_price') }}
                            </base-radio>
                        </div>
                        <div class="form-check">
                            <base-radio v-model="price" name="DESC">
                                {{ $t('cart.max_price') }}
                            </base-radio>
                        </div>
                    </div>
                </div>
            </div>
            <div class="container d-flex justify-content-end">
                <button
                    @click="$emit('cleanFilter')"
                    class="btn btn-link d-flex align-items-center"
                    style="cursor: pointer"
                >
                    <i class="mdi mdi-24px mdi-close"/>
                    <span :class="hideOnSmall">
                        {{ $t('cart.clean_filters') }}
                    </span>
                </button>
            </div>
        </div>
    </collapse-transition>
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { CollapseTransition } from 'vue2-transitions';

import BaseCheckbox from '../argon/components/Inputs/BaseCheckbox';
import BaseRadio from '../argon/components/Inputs/BaseRadio';
import CurrencySelect from '../components/CurrencyComponents/CurrencySelect';

export default {
    emits: ['categoryFilter', 'cleanFilter', 'currency', 'facetFilter', 'input', 'orderFilter', 'searchInput', 'update:modelValue'],
    name: 'Search',
    components: {
        CollapseTransition,
        BaseCheckbox,
        BaseRadio,
        CurrencySelect,
    },

    props: {
        modelValue: Array,
        categories: Array,
        planCategories: Array,
        planFacets: { type: Array, default: () => [] },
        selectedFacets: { type: Object, default: () => ({}) },
        filters: Boolean,
        currency: Boolean,
        showSearch: {
            type: Boolean,
            default: true,
        },
        noFixed: {
            type: Boolean,
            default: false,
        },
        availableYears: { type: Array, default: () => [] },
        selectedYear: { type: [String, Number], default: '' },
    },

    data() {
        return {
            show: false,
            search: '',
            price: '',
        };
    },

    computed: {
        ...mapGetters({
            language: 'LANGUAGE',
            layout: 'NAVBAR_FIXED',
        }),
        planType: {
            get() { return this.modelValue; },
            set(value) { this.$emit('update:modelValue', value); },
        },
        planCategory: {
            get() { return this.planCategories; },
            set(value) { this.$emit('categoryFilter', value); },
        },
        hideOnSmall() {
            if (this.$mq === 'sm') return 'hide';
            return null;
        },
        hasBilling() {
            return this.$getSettings.clientSettings && this.$getSettings.clientSettings.app && this.$getSettings.clientSettings.app.billing;
        },
        visibleFacets() {
            return (this.planFacets || [])
                .filter(f => f && f.key && Array.isArray(f.values) && f.values.length);
        },
    },

    methods: {
        removeChecks(value) {
            if (value) { this.planCategory = [0]; } else { this.planCategory = []; }
        },
        facetValues(key) {
            return (this.selectedFacets && this.selectedFacets[key]) || [];
        },
        onFacetInput(key, values) {
            const next = { ...(this.selectedFacets || {}) };
            if (values && values.length) next[key] = values;
            else delete next[key];
            this.$emit('facetFilter', next);
        },
    },

    watch: {
        price(value) {
            this.$emit('orderFilter', value);
        },
    },
};
</script>

<style scoped>

.category-parent,
.facet-parent {
    max-height: 140px;
    overflow-y: auto;
}
.hide {
    display: none;
}

.nch--search-container {
    width: 100%;
    height: 70px;
    background-color: var(--page-surface, #ebebeb);
    border-top: 3px solid var(--page-border, #a1a1a1);
    color: var(--page-text, inherit);
    transition: background-color 0.3s, color 0.3s;
}

.nch--search-container i,
.nch--search-container span {
    color: var(--page-text, inherit);
}

.nch--search-container a {
    color: var(--page-text, inherit) !important;
}

.bg-transparent {
    background-color: transparent !important;
    box-shadow: none;
}

.nch--search-input {
    width: 200px !important;
    border: none !important;
    border-radius: 0 !important;
    border-bottom: 1.5px solid var(--page-text, #525F7F) !important;
    color: var(--page-text, #212121) !important;
    transition: width 0.3s !important;
}

.nch--search-input:focus {
    width: 250px !important;
}

.nch--search-input::placeholder {
    color: var(--page-text-secondary, #6c757d) !important;
    opacity: 1;
}

.filter-container {
    position: relative;
    background-color: var(--page-surface, #ebebeb);
    color: var(--page-text, inherit);
    transition: background-color 0.3s;
}

.filter-container h2,
.filter-container span,
.filter-container label,
.filter-container i {
    color: var(--page-text, inherit) !important;
}

.filter-container .btn-link {
    color: var(--page-text, inherit) !important;
}

.filter-container--fixed {
    z-index: 10;
    top: 202px;
    width: 100%;
    position: fixed;
    animation-fill-mode: both;
    animation-timing-function: ease-out;
}

.nch-search--fixed {
    top: 132px;
    z-index: 10;
    position: fixed;
}

@media (max-width: 768px) {
    .nch-search--fixed {
        top: 111px;
    }
}

.nch-year-select {
    max-width: 180px;
    border-radius: 6px;
    border: 1px solid #ccc;
    padding: 4px 8px;
    font-size: 0.85rem;
    color: var(--page-text, #212121);
    background: var(--page-surface, #fff);
}
</style>
